import { PaymentMethod } from '../../types/constants';

import { isMAIPasPDF } from './helpers';

import type { FUNDING_SOURCE } from '@paypal/paypal-js';
import type { RawAxiosRequestHeaders } from 'axios';
import type { Sale } from 'types/Sale';
import type { Wallet } from 'types/Wallet';

import { reportPaymentNewSyntax } from 'businessLogic/Insight/Reporting';
import HttpClient from 'server/helpers/HttpClient';
import { getInitialSelectedPaymentMethod, getWeakTicket } from 'shared/clientUtils';
import { PayPalOrderIds, invoiceInfo, paypalOrderData } from 'types/BusinessLogic';
import { FeatureFlags } from 'types/FeatureFlags';
import { IXP } from 'types/IXP';
import { Payment } from 'types/Payment';

const { SplunkReporter } = require('reporting/splunk/SplunkReporter');
const splunkReporter = SplunkReporter.getInstance();
const logger = 'businessLogic/Payment/Paypal';
const httpClient = HttpClient.getInstance();
export const handlePaypalOrder = async ({
  action,
  intuitOrderId,
  portal,
  headers,
  authToken,
  amount,
  currencyCode,
  isGpu,
  subType,
  displayName,
  toEmails,
  contactDetailsName,
  contactDetailsEmail,
  profileToken,
  usedPaymentMethod,
  gratuityAmount = 0,
  type,
  contact,
  additionalPayorRecipient,
}: {
  action: 'CREATE' | 'CONFIRM';
  intuitOrderId?: string;
  portal: string;
  headers: RawAxiosRequestHeaders;
  authToken: string;
  amount: number;
  currencyCode: string;
  isGpu: boolean;
  subType?: Sale['subType'];
  displayName?: Sale['contact']['displayName'];
  toEmails?: Sale['contact']['toEmails'];
  contactDetailsName?: Payment['contactDetailsName'];
  contactDetailsEmail?: Payment['contactDetailsEmail'];
  usedPaymentMethod: string;
  gratuityAmount: number;
  profileToken: string;
  type: Sale['type'];
  ssrtid: string;
  token: string;
  featureFlags: FeatureFlags;
  contact: Sale['contact'];
  additionalPayorRecipient?: string;
}) => {
  const method = convertFundingSourceToPaymentMethod(usedPaymentMethod);

  try {
    if (amount < 1) {
      throw new Error('cannot pay amount < 1');
    }
    const endpoint = action === 'CREATE' ? 'create-order' : `confirm-order/${intuitOrderId}`;
    let payorName = displayName,
      payorEmail = toEmails;
    if (subType === 'PAYMENT_LINK_MULTIPLE' && contactDetailsName && contactDetailsEmail) {
      payorName = contactDetailsName;
      payorEmail = [contactDetailsEmail];
    } else if (isMAIPasPDF({ sale: { type, contact } }) && contactDetailsEmail) {
      payorEmail = [contactDetailsEmail];
    }
    const data: paypalOrderData = {
      payment: {
        method,
        amount: amount.toFixed(2),
        currencyCode,
      },
      risk: {
        profileToken,
      },
      sale: {
        type,
        isGpu: isGpu,
        subType,
      },
      contact: {
        name: payorName,
        emails: payorEmail,
      },
    };
    if (gratuityAmount > 0) {
      data.payment.gratuityAmount = gratuityAmount;
    }
    if (additionalPayorRecipient) {
      data.payment.additionalPayorRecipient = additionalPayorRecipient;
    }
    //@ts-ignore
    headers['Authorization'] = `Bearer ${authToken}`;
    const handlePaypalOrderResponse = await httpClient({
      url: `/${portal}/rest/paypal/ppaam/${endpoint}`,
      endpoint: `/${portal}/rest/paypal/ppaam/${
        action === 'CREATE' ? 'create-order' : 'confirm-order/{intuitOrderId}'
      }`,
      method: 'POST',
      data,
      headers,
      timeout: 60000,
    });
    return handlePaypalOrderResponse;
  } catch (e: any) {
    const { message = '' } = e;
    return { status: 'ERROR', message };
  }
};

export const checkIFPaypalEnabled = ({
  sourceOffering,
  saleType,
  enabledPaymentMethods: originalEnabledPaymentMethods = [],
  featureFlags = {},
  paymentMethodType: currentSelectedPaymentMethod,
}: {
  sourceOffering: string;
  saleType: Sale['type'];
  userWallets: Wallet['userWallets'];
  enabledPaymentMethods: Array<string>;
  shouldShowSchedulePay: boolean;
  shouldShowAutopay: boolean;
  featureFlags: Record<string, boolean>;
  paymentMethodType: string | null;
  ixp: IXP;
}) => {
  try {
    const isPaymentRequest = saleType === 'PAYMENT_REQUEST';
    const isQBOInvoice = saleType === 'INVOICE' && ['QBO', 'QBMONEY'].includes(sourceOffering);
    const isSaleTypeSupported = isPaymentRequest || isQBOInvoice;
    const __isEligibleAccordingToPaypal = (fundingSource: FUNDING_SOURCE) => {
      if (typeof window === 'undefined' || !window.paypal) {
        throw new Error(`window.paypal is empty, typeof window = ${typeof window}`);
      }
      // @ts-ignore TODO CPV2-5741 throw error if (!window.paypal)
      const paypalFundingSource = window.paypal.Buttons({ fundingSource });
      const isFundingSourceEligible =
        paypalFundingSource.isEligible && paypalFundingSource.isEligible();
      paypalFundingSource &&
        paypalFundingSource.close &&
        paypalFundingSource.close().catch((e: any) => {
          const knownErrors = [
            'Detected container element removed from DOM',
            'Component closed',
            'Close is not a function',
            'L.onClose is not a function',
            `L.onClose is not a function. (In 'L.onClose()', 'L.onClose' is undefined)`,
          ];
          if (!knownErrors.includes(e.message)) {
            splunkReporter.contextual({
              logInfo: { logLevel: 'error', logger },
              event: 'viewSale',
              action: 'detectPayPalEligibility',
              activityInfo: {
                status: 'error',
                initialEnabledPaymentMethods: originalEnabledPaymentMethods,
                enabeldPaymentMethods: originalEnabledPaymentMethods,
              },
              error: {
                stack: e.stack,
                message: e.message,
              },
            });
          }
        });
      return isFundingSourceEligible;
    };
    const __checkIsEligibleForSale = (fundingSource: FUNDING_SOURCE) =>
      isSaleTypeSupported && __isEligibleAccordingToPaypal(fundingSource);
    let updatedPaymentMethodType = currentSelectedPaymentMethod;

    // In case we are disabling paypal then we need to also change the selected payment method
    const __updateSelectedPaymentMethod = (paymentMethod: string) => {
      if (paymentMethod !== currentSelectedPaymentMethod) {
        return currentSelectedPaymentMethod;
      }
      // @ts-ignore
      updatedPaymentMethodType = getInitialSelectedPaymentMethod({
        enabledPaymentOptions: enabledPaymentMethods as any,
      });
      return updatedPaymentMethodType;
    };

    let enabledPaymentMethods = originalEnabledPaymentMethods;

    const checkEligibilityByPaymentMethodMap: {
      fundingSource: FUNDING_SOURCE;
      paymentMethod: PaymentMethod;
      featureFlag: string;
    }[] = [
      { fundingSource: 'venmo', paymentMethod: 'venmo', featureFlag: 'block-venmo-ppaam-payments' },
      {
        fundingSource: 'paypal',
        paymentMethod: 'paypal_ppaam',
        featureFlag: 'block-paypal-ppaam-payments',
      },
      {
        fundingSource: 'paylater',
        paymentMethod: 'paypal_ppaam paylater',
        featureFlag: 'block-paypal-ppaam-paylater-payments',
      },
    ];

    checkEligibilityByPaymentMethodMap.forEach(({ fundingSource, paymentMethod, featureFlag }) => {
      if (!__checkIsEligibleForSale(fundingSource) || featureFlags[featureFlag]) {
        enabledPaymentMethods = enabledPaymentMethods.filter((item) => item !== paymentMethod);
        __updateSelectedPaymentMethod(paymentMethod);
      }
    });

    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'viewSale',
      action: 'detectPayPalEligibility',
      activityInfo: {
        status: 'success',
        initialEnabledPaymentMethods: originalEnabledPaymentMethods,
        enabeldPaymentMethods: enabledPaymentMethods,
        isPayPalEnabled: enabledPaymentMethods.includes('paypal_ppaam'),
        isVenmoEnabled: enabledPaymentMethods.includes('venmo'),
      },
    });
    return {
      enabledPaymentMethods,
      paymentMethodType: updatedPaymentMethodType,
    };
  } catch (e: any) {
    splunkReporter.contextual({
      logInfo: { logLevel: 'error', logger },
      event: 'viewSale',
      action: 'detectPayPalEligibility',
      activityInfo: {
        status: 'error',
        initial_enabled_payment_methods: originalEnabledPaymentMethods,
        enabeld_payment_methods: originalEnabledPaymentMethods,
      },
      error: {
        stack: e.stack,
        message: e.message,
      },
    });

    return {
      enabledPaymentMethods: originalEnabledPaymentMethods,
      paymentMethodType: currentSelectedPaymentMethod,
    };
  }
};

export const finalizeOrderOnPayPalCommerce = async (data: PayPalOrderIds, paymentsValue: any) => {
  const {
    domainId,
    token,
    transactionType,
    companyLocale,
    currency,
    customerName,
    auth: { entityId, realmId, ticket, isUserSignedIn, syncToken, authToken, recipientEmail },
    config: { ssrtid, portal },
    payment: {
      amount,
      isPayPalCommerceInvoice,
      ppCaptureOrderKey,
      payPalProcessor,
      contactDetailsEmail,
    },
  } = paymentsValue;
  const { orderID, payerID, orderId } = data;
  const currencyCode = currency;
  let orderFromPPC;
  if (orderID) {
    orderFromPPC = orderID;
  } else {
    orderFromPPC = orderId;
  }
  if (data.notAllowedPayPalCardPayment) {
    const error = new Error('3DS failed for the payer, stopping payment');
    let invoiceInfo: invoiceInfo = {
      ssrtid,
      companyLocale,
      token,
      ppCaptureOrderKey,
    };
    Object.assign(error, invoiceInfo);
    throw error;
  }
  try {
    let WeakTicketScsHeaders,
      paypalPaymentContext = {};

    WeakTicketScsHeaders = getWeakTicket({
      domainId,
      entityId,
      realmId,
      token,
      ticket,
      isUserSignedIn,
      syncToken,
      authToken,
      ssrtid,
    });
    paypalPaymentContext = {
      isNewPaypalFlow: isPayPalCommerceInvoice,
      paypalOrderId: orderFromPPC,
      payerId: payerID,
      ppCaptureOrderKey,
      paypalToken: orderFromPPC,
    };
    const customerEmail = recipientEmail || contactDetailsEmail;
    const capturePayPalPayment = await httpClient({
      url: `/${portal}/app/CommerceNetwork/view/rest/external-payment/capture-payment/paypal`,
      method: 'POST',
      headers: WeakTicketScsHeaders,
      endpoint: `/${portal}/app/CommerceNetwork/view/rest/external-payment/capture-payment/paypal`,
      data: {
        transactionType,
        companyLocale,
        amount,
        currencyCode,
        customerName,
        paypalPaymentContext,
        sub_flow: 'PayPal_Commerce_SCS',
        contactInfo: {
          name: customerName,
          emails: [customerEmail],
        },
      },
      ssrtid,
      timeout: 30000,
      token,
      event: 'pay',
    });
    const {
      data: { errorMsg, success, paymentStatus, trackingId, jwt, currencySymbol: currency },
    } = capturePayPalPayment;
    if (success || paymentStatus === 'SUCCESS') {
      let result = {
        data: {
          jwt,
          paymentMethod: 'PayPalCommerce',
          paymentStatus: 'SUCCESS',
          trackingId,
          // the date returned from capturePay API has invalid pattern
          paymentDate: Date.now(),
          amountPaid: amount,
          savePaymentMethodSuccessful: false,
          successful: success,
          creditCardWalletPayment: false,
          bankPayment: false,
          cardPayment: false,
          payPalPayment: true,
          bankAccountWalletPayment: false,
        },
      };

      //@ts-ignore
      reportPaymentNewSyntax({
        amountPaid: amount,
        currency,
        intuit_tid: entityId,
        paymentMethod: 'PayPal',
        paymentStatus,
        paymentType: 'scsPayment',
        trackingId,
        payPalProcessor,
        isPayPalCommerceInvoice,
      });

      return result;
    } else {
      const error = new Error(errorMsg);
      let invoiceInfo: invoiceInfo = {
        ssrtid,
        companyLocale,
        token,
        ppCaptureOrderKey,
      };
      Object.assign(error, invoiceInfo);
      throw error;
    }
  } catch (error: any) {
    error.invoiceInfo = {
      ssrtid,
      companyLocale,
      token,
      ppCaptureOrderKey,
    };
    throw error;
  }
};

export const shouldShowSchedulePayDisabledMessage = ({
  enabledPaymentMethods,
  isUserSignedIn,
  paymentMethodType,
}: {
  enabledPaymentMethods: [string];
  isUserSignedIn: boolean;
  paymentMethodType: string;
}) => {
  try {
    // External methods where schedule/autopay are not available
    const externalMethods = ['paypal_ppaam', 'venmo', 'ap', 'affirm_bnpl'];

    const isExternalSelection = externalMethods.includes(paymentMethodType);
    // If every enabled method is external, we do not show the banner
    const hasOnlyExternalMethods = enabledPaymentMethods.every((method) =>
      externalMethods.includes(method)
    );

    return isUserSignedIn && isExternalSelection && !hasOnlyExternalMethods;
  } catch (e) {
    return false;
  }
};

interface SafariNavigator extends Navigator {
  standalone?: boolean;
}

export const shouldShowVenmoInegratedBrowswerMessage = ({
  paymentMethodType,
}: {
  paymentMethodType: string;
}) => {
  if (typeof window !== 'object') return;
  const perfNavigator: SafariNavigator = window.navigator;
  return !!(paymentMethodType === 'venmo' && perfNavigator?.standalone);
};

export const isPayPalSetOnTheWindow = (paymentMethodType: string) => {
  if (typeof window === 'undefined' || !window.paypal) {
    splunkReporter.contextual({
      logInfo: { logLevel: 'error', logger },
      event: 'viewSale',
      action: 'detectPayPalEligibility',
      activityInfo: {
        status: 'error',
        paymentMethodType,
      },
      error: {
        message: `window.paypal is empty, typeof window = ${typeof window}`,
      },
    });
    return false;
  }
  return true;
};

const convertFundingSourceToPaymentMethod = (fundingSource: string) => {
  const fundingSourceConverted = typeof fundingSource === 'string' && fundingSource.toLowerCase();
  return ['paypal', 'paylater'].includes(fundingSourceConverted as string)
    ? 'PAYPAL_PPAAM'
    : 'VENMO_PPAAM';
};
